import { type Entity, splitEntity } from "shelving";
import { Icon, type IconType, MediaIcon, MediaImage } from "shared/react";
import { type ContentType, getGroupTitle, getProfileTitle, isContentType } from "shared/schema";
import { getMediaImageStyle } from "shared/util/style";
import { useItem } from "../util/db.js";
import { getRewardStyle } from "../util/reward.js";

/** Show a photo for an entity. */
export function EntityPhoto({ entity, mods }: { entity: Entity; mods?: string | undefined }): React.ReactElement | null {
	const [type, id] = splitEntity(entity);
	if (type === "profile") return <ProfilePhoto uid={id} mods={mods} />;
	else if (type === "group") return <GroupPhoto gid={id} mods={mods} />;
	else if (type === "reward") return <RewardPhoto id={id} mods={mods} />;
	else if (isContentType(type)) return <CreatedEntityPhoto type={type} id={id} />;
	else
		return (
			<figure className={`photo ${mods}`}>
				<Icon icon={type as IconType} />
			</figure>
		);
}

/** Show a photo for the creator of an created entity. */
export function CreatedEntityPhoto({ type, id, mods }: { type: ContentType; id: string; mods?: string }): React.ReactElement {
	const { creator } = useItem(type, id).data;
	return <EntityPhoto entity={creator} mods={mods} />;
}

/** Show a profile photo, or a coloured profile icon if the user has no photo. */
export function ProfilePhoto({ uid, mods = "" }: { uid: string; mods?: string | undefined }): React.ReactElement {
	const profile = useItem("profile", uid).data;
	const { photo, background } = profile;
	const reward = useItem("reward", background);
	return (
		<figure className={`photo ${mods}`} style={reward?.exists === true ? getRewardStyle(reward.data) : undefined} title={getProfileTitle(profile)}>
			{photo ? <MediaImage path={photo} /> : <Icon icon="profile" />}
		</figure>
	);
}

/** Show a group photo, or a coloured group icon if the group has no photo. */
export function GroupPhoto({ gid, mods = "" }: { gid: string; mods?: string | undefined }): React.ReactElement {
	const group = useItem("group", gid).data;
	const { photo, cover, hue } = group;
	return (
		<figure className={`photo ${mods}`} style={getMediaImageStyle(cover, hue)} title={getGroupTitle(group)}>
			{photo ? <MediaImage path={photo} /> : <Icon icon="chat" />}
		</figure>
	);
}

/** Show a reward image as a photo. */
export function RewardPhoto({ id, mods = "pad-hairline" }: { id: string; mods?: string | undefined }): React.ReactElement {
	const { name, image, type, hue } = useItem("reward", id).data;
	return type === "background" ? (
		<figure className={`photo ${mods}`} style={getMediaImageStyle(image, hue)} title={name}>
			<Icon icon="background" />
		</figure>
	) : (
		<figure className={`photo ${mods}`} style={getMediaImageStyle(undefined, hue)} title={name}>
			<MediaIcon path={image} />
		</figure>
	);
}
